/**
 * fulfillmentExport.ts — turns this app's BQC1–BQC5 data into the five sheets
 * the fulfillment team's ERP already knows how to import.
 *
 * WHY AN EXPORT AND NOT A DIRECT WRITE. The ERP is Flask on PostgreSQL running
 * on a PC on the office LAN, reachable only as http://<name>:5000. A browser on
 * an https page cannot POST to it: that is active mixed content, which is a
 * hard block rather than a warning, and the ERP sends no CORS headers either.
 * So the handover is a file, the same shape as the four AutoWrite* outboxes in
 * this folder already produce for the legacy workbooks.
 *
 * WHY THESE EXACT HEADERS. They are not ours to choose. The ERP's importers
 * (/import_manufacturing, /import_cutting, /import_assigning,
 * /import_receiving, /import_qc_passed, /import_qc_failed) map incoming
 * columns by their DISPLAY NAME against its own COLUMN_MAP tables. A renamed
 * or reordered column is silently dropped, not rejected, so the strings below
 * are a contract with that code and must match it character for character,
 * "WIP No." and its trailing period included.
 *
 * SOURCE OF TRUTH IS IProductionWip, not the per-station scan lists. The grid
 * parser already merges the hub workbook's own history with this app's scans
 * into one WIP's own fields, and for any bundle that moved through a station
 * before that station existed in this app the workbook column is the ONLY
 * record — see the grn, qcPassedQty and qcPersonnel comments on
 * IProductionWip. Reading the scan lists instead would silently export only
 * the recent tail.
 *
 * THAT IS NOT THE SAME AS ONE ROW PER WIP IN THE LIST. `iSyn_WIPs` genuinely
 * holds the same WIP number more than once — every other screen that reads it
 * (Board.tsx, Dispatch.tsx, controlTower.ts, woLogRecon.ts…) has its own
 * dedupe for exactly that reason. `exportableWips` below is this file's,
 * reusing woLogRecon's own rule (furthest stage wins) rather than an
 * arbitrary "first one SharePoint returns" — found by a real Check sending
 * back roughly HALF of every station's rows as duplicates within one payload.
 */
import type { IProductionWip } from "../../lib/sp/ProductionGridService";
import { dedupeWips } from "./woLogRecon";

/**
 * One ERP table: the page it uploads to, the file it travels as, its headers,
 * and its rows.
 *
 * SIX SEPARATE FILES, NOT ONE WORKBOOK WITH SIX TABS. Every importer on their
 * side calls pd.read_excel(filepath) with no sheet_name, and pandas then reads
 * the FIRST sheet and ignores the rest. A single six-tab workbook uploaded to
 * all six pages would import the Manufacturing Order columns six times over,
 * five of them into the wrong table. The file name is the route name for the
 * same reason: at upload time there is then nothing to work out.
 *
 * Their allowed extensions are xlsx and xls only, with a 10 MB cap per upload.
 */
export interface IErpSheet {
  /** Which station this is, for labelling and for the default selection. */
  stage: "BQC1" | "BQC2" | "BQC3" | "BQC4" | "BQC5";
  /** Human name, shown in the UI and used as the sheet name inside its file. */
  name: string;
  /**
   * True when the ERP already maintains this table itself, so sending ours
   * would duplicate rather than inform. Only BQC1: their app has its own
   * Manufacturing Order page, its own /import_manufacturing route, and derives
   * its For Cutting list from that table. Manufacturing owns BQC1 upstream of
   * both of us, but THEY already receive it, so it is off by default here and
   * ticked only deliberately.
   */
  alreadyTheirs?: boolean;
  /** The ERP page this uploads to, e.g. "/cutting". */
  importPath: string;
  /** File name inside the zip. Numbered for import order, named for the
   * route, so the person uploading matches file to page by reading it. */
  fileName: string;
  /** Display headers, in order. The ERP matches on these strings. */
  headers: readonly string[];
  rows: Record<string, string>[];
}

/**
 * ISO timestamp to the YYYY-MM-DD the ERP now stores. Its date columns are
 * plain TEXT, so migrate_dates_to_iso.py rewrote the live data to ISO purely so
 * it sorts correctly as text -- under the old M/D/YYYY, "10/1/2026" sorted
 * before "2/1/2026". Its importers do still accept M/D/YYYY, via a pandas parse
 * in db.to_iso_date, but writing ISO ourselves is strictly safer: it removes the
 * dependence on pandas reading a slash date month-first, and to_iso_date returns
 * anything it cannot parse UNCHANGED, which would quietly seat a non-ISO string
 * in a column the app now sorts as ISO text. Returns "" for blank or
 * unparseable input rather than a sentinel
 * date: an empty cell reads as "this has not happened", which is true, whereas
 * any placeholder date would read as a real event on the wrong day.
 *
 * Deliberately uses the UTC components of the parsed instant. These stamps are
 * dates in practice, and formatting them in the viewer's local zone would slide
 * a late-evening Manila scan onto the previous day for anyone reading the file
 * elsewhere.
 */
export function toErpDate(iso: string | undefined | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${month}-${day}`;
}

/** A number for a cell the ERP stores as text. 0 and undefined differ: an
 * untouched quantity is blank, a real zero is "0". */
function num(v: number | undefined | null): string {
  return v === undefined || v === null ? "" : String(v);
}

function str(v: string | undefined | null): string {
  return (v ?? "").trim();
}

/**
 * The cutter columns. BQC1's ASSIGNED CUTTER stopped naming people some time
 * ago and carries a SKU-velocity tag now (FAST / STEADY / FLEX) or a table
 * name, which is what the ERP's own "Assigned Cutter" holds too — its live
 * rows show both "SONGUAD" and "FAST" in that column. The PEOPLE live in the
 * ERP's Cutter 1/2/3, and on our side in cutOperator, where two people at one
 * table are recorded as one slash-joined value ("MILAN/PERPETUA").
 */
export function splitCutters(cutOperator: string | undefined): [string, string, string] {
  const parts = str(cutOperator)
    .split("/")
    .map((p) => p.trim())
    .filter(Boolean);
  return [parts[0] ?? "", parts[1] ?? "", parts[2] ?? ""];
}

/** Stage order, for "has this WIP reached at least X" questions. DONE counts
 * as past every station; LOST and DAMAGED do not advance and are excluded
 * from the stage sheets by reachedStage returning false for them. */
const STAGE_ORDER: Record<string, number> = {
  BQC1: 1, BQC2: 2, BQC3: 3, BQC4: 4, BQC5: 5, DONE: 6,
};

function reachedStage(wip: IProductionWip, stage: keyof typeof STAGE_ORDER): boolean {
  const at = STAGE_ORDER[wip.currentStage ?? ""] ?? 0;
  return at >= STAGE_ORDER[stage];
}

// ── BQC1 · Manufacturing Order ───────────────────────────────────────────────

const MANUFACTURING_HEADERS = [
  "WIP No.", "Order No.", "SKU Code", "Assigned Cutter", "Channel", "Serial No.",
  "Due Date", "Design", "Color", "Size", "Qty", "Assigned On",
] as const;

/**
 * The master list. Every WIP goes, whatever stage it is at, because this is
 * the sheet the ERP's other tables join against on WIP No.
 *
 * The four stage columns the ERP also keeps here (Cutting Remarks, Assigning
 * Remarks, and so on) are deliberately NOT sent. They are its own workflow
 * state, written by its own staff as they work a row; overwriting them from
 * here would reach across the handover and undo their work.
 */
export function buildManufacturingRows(wips: IProductionWip[]): Record<string, string>[] {
  return wips.map((w) => ({
    "WIP No.": str(w.wipId),
    "Order No.": str(w.orderId),
    "SKU Code": str(w.sku),
    "Assigned Cutter": str(w.cutter),
    "Channel": str(w.storeChannel),
    "Serial No.": str(w.serialId),
    "Due Date": toErpDate(w.dueDate),
    "Design": str(w.design),
    "Color": str(w.color),
    "Size": str(w.size),
    "Qty": num(w.qty),
    "Assigned On": toErpDate(w.dateAssignedToCutter || w.dateCreated),
  }));
}

// ── BQC2 · Cutting ───────────────────────────────────────────────────────────

const CUTTING_HEADERS = [
  "Cutting Date", "WIP No.", "Order No.", "SKU Code", "Assigned Cutter", "Channel",
  "Serial No.", "Due Date", "Design", "Color", "Size", "Qty",
  "Cutter 1", "Cutter 2", "Cutter 3",
] as const;

export function buildCuttingRows(wips: IProductionWip[]): Record<string, string>[] {
  return wips.filter((w) => reachedStage(w, "BQC2")).map((w) => {
    const [c1, c2, c3] = splitCutters(w.cutOperator);
    return {
      "Cutting Date": toErpDate(w.dateAssignedToCutter),
      "WIP No.": str(w.wipId),
      "Order No.": str(w.orderId),
      "SKU Code": str(w.sku),
      "Assigned Cutter": str(w.cutter),
      "Channel": str(w.storeChannel),
      "Serial No.": str(w.serialId),
      "Due Date": toErpDate(w.dueDate),
      "Design": str(w.design),
      "Color": str(w.color),
      "Size": str(w.size),
      "Qty": num(w.qty),
      "Cutter 1": c1,
      "Cutter 2": c2,
      "Cutter 3": c3,
    };
  });
}

// ── BQC3 · Assigning ─────────────────────────────────────────────────────────

const ASSIGNING_HEADERS = [
  "DS No.", "WIP No.", "Serial No.", "SKU Code", "Design", "Color", "Size",
  "Qty", "Due Date", "Assigned Sewer", "Assigning Date",
] as const;

/**
 * Keyed on the DS number actually being present rather than on the stage.
 * A dispatch slip with no number on it is not something the ERP can reconcile
 * a receipt against, so sending the row would create an orphan.
 */
export function buildAssigningRows(wips: IProductionWip[]): Record<string, string>[] {
  return wips.filter((w) => str(w.assignedDS) !== "").map((w) => ({
    "DS No.": str(w.assignedDS),
    "WIP No.": str(w.wipId),
    "Serial No.": str(w.serialId),
    "SKU Code": str(w.sku),
    "Design": str(w.design),
    "Color": str(w.color),
    "Size": str(w.size),
    "Qty": num(w.qty),
    "Due Date": toErpDate(w.dueDate),
    "Assigned Sewer": str(w.assignedSewer),
    "Assigning Date": toErpDate(w.dispatchedAt),
  }));
}

// ── BQC4 · Receiving ─────────────────────────────────────────────────────────

const RECEIVING_HEADERS = [
  "GRN No.", "DS No.", "Receiving Date", "Qty Received", "WIP No.", "SKU Code",
  "Design", "Color", "Size", "Qty", "Channel", "Serial No.",
  "Assigned Sewer", "Assigning Date", "Order Status",
] as const;

export function buildReceivingRows(wips: IProductionWip[]): Record<string, string>[] {
  return wips
    .filter((w) => Boolean(w.receivedAt) || (w.receivedQty ?? 0) > 0)
    .map((w) => ({
      "GRN No.": str(w.grn),
      "DS No.": str(w.assignedDS),
      "Receiving Date": toErpDate(w.receivedAt),
      "Qty Received": num(w.receivedQty),
      "WIP No.": str(w.wipId),
      "SKU Code": str(w.sku),
      "Design": str(w.design),
      "Color": str(w.color),
      "Size": str(w.size),
      "Qty": num(w.qty),
      "Channel": str(w.storeChannel),
      "Serial No.": str(w.serialId),
      "Assigned Sewer": str(w.assignedSewer),
      "Assigning Date": toErpDate(w.dispatchedAt),
      // The ERP's own column. COMPLETED only when the full quantity came back;
      // a short return is left blank rather than guessed at, because the ERP
      // reconciles partials itself and a wrong COMPLETED would close a row
      // that is still open on their floor.
      "Order Status": (w.receivedQty ?? 0) >= w.qty && w.qty > 0 ? "COMPLETED" : "",
    }));
}

// ── BQC5 · Quality Control ───────────────────────────────────────────────────

const QC_PASSED_HEADERS = [
  "QC Date", "QC Qty", "QC Personnel", "WIP No.", "SKU Code", "Design", "Color",
  "Size", "Qty", "Channel", "Serial No.", "Assigned Sewer", "Assigning Date", "Status",
] as const;

export function buildQcPassedRows(wips: IProductionWip[]): Record<string, string>[] {
  return wips.filter((w) => (w.qcPassedQty ?? 0) > 0).map((w) => ({
    "QC Date": toErpDate(w.qcPassedAt),
    "QC Qty": num(w.qcPassedQty),
    "QC Personnel": str(w.qcPersonnel),
    "WIP No.": str(w.wipId),
    "SKU Code": str(w.sku),
    "Design": str(w.design),
    "Color": str(w.color),
    "Size": str(w.size),
    "Qty": num(w.qty),
    "Channel": str(w.storeChannel),
    "Serial No.": str(w.serialId),
    "Assigned Sewer": str(w.assignedSewer),
    "Assigning Date": toErpDate(w.dispatchedAt),
    "Status": "PASSED",
  }));
}

const QC_FAILED_HEADERS = [
  "QC Date", "QC Qty", "QC Personnel", "WIP No.", "SKU Code", "Design", "Color",
  "Size", "Qty", "Channel", "Serial No.", "Assigned Sewer", "Assigning Date",
  "Status", "Reject Reason", "Remarks",
] as const;

/**
 * Driven by qcRepairQty, which counts sets sent back for rework. Note it is an
 * EVENT tally and is not capped at the WIP's quantity on purpose: a two-set
 * bundle that failed twice really did put four sets through the repair loop.
 * Passing it straight through keeps the ERP's figure equal to ours rather than
 * quietly reinterpreting it.
 *
 * Reject Reason and Remarks are left blank. This app does not capture a reason
 * code at BQC5, and inventing one would be worse than an empty cell the ERP's
 * own staff can fill.
 */
export function buildQcFailedRows(wips: IProductionWip[]): Record<string, string>[] {
  return wips.filter((w) => (w.qcRepairQty ?? 0) > 0).map((w) => ({
    "QC Date": toErpDate(w.qcPassedAt),
    "QC Qty": num(w.qcRepairQty),
    "QC Personnel": str(w.qcPersonnel),
    "WIP No.": str(w.wipId),
    "SKU Code": str(w.sku),
    "Design": str(w.design),
    "Color": str(w.color),
    "Size": str(w.size),
    "Qty": num(w.qty),
    "Channel": str(w.storeChannel),
    "Serial No.": str(w.serialId),
    "Assigned Sewer": str(w.assignedSewer),
    "Assigning Date": toErpDate(w.dispatchedAt),
    "Status": "FAILED",
    "Reject Reason": "",
    "Remarks": "",
  }));
}

// ── The workbook ─────────────────────────────────────────────────────────────

/**
 * Cancelled WIPs never go. The ERP has its own cancelled-orders table fed by
 * its own process, and a cancelled bundle arriving through a stage import
 * would read as live work in their queue.
 */
/**
 * True when a WIP would contribute at least one row to the five sendable
 * tables — i.e. it has reached CUTTING.
 *
 * A WIP still at BQC1 produces a Manufacturing Order row and nothing else,
 * because every other builder is gated on its stage. So a batch raised this
 * week has 79 WIPs and nothing to hand over, which is correct and looked like
 * a broken screen the first time it happened (Mikko, 2026-09-22: picked
 * WK 0520-0922 and got five tables of 0 rows with both buttons dead).
 *
 * Exported so the batch picker can say which batches have something in them
 * rather than leaving somebody to find out one at a time.
 */
/**
 * The four timestamps that put a WIP on one of the five sendable tables, in
 * BQC order. These ARE the dates their own screens group by, so a scope built
 * on them answers "what happened on this day" the way fulfillment asks it.
 */
export function stationDates(wip: IProductionWip): (string | undefined)[] {
  return [
    wip.dateAssignedToCutter,  // BQC2 Cutting Date
    wip.dispatchedAt,          // BQC3 Assigning Date
    wip.receivedAt,            // BQC4 Receiving Date
    wip.qcPassedAt,            // BQC5 QC Date
  ];
}

/**
 * True when a WIP did something at a station ON OR AFTER `sinceIso`.
 *
 * ── WHY A DATE AND NOT A BATCH ────────────────────────────────────────────
 * A batch is a MANUFACTURING grouping — a cutting week. Fulfillment does not
 * have batches and does not care about ours; they read what happened, by day.
 * Mikko, 2026-09-22: "why is it per BATCH? i think its better if we based on a
 * daily basis since BQC1 to BQC5 of Manufacturing is the action while
 * fulfillment is only the reader of what manufacturing doing."
 *
 * The batch scope existed because the ZIP has a 10 MB cap per file and a batch
 * kept the files small. The direct route chunks instead, so the reason is gone.
 *
 * Judged on the WIP's own station timestamps rather than its batch or its
 * creation date: a bundle cut in May and QC'd today IS today's work on the
 * QC table, and belongs in a scope that says today.
 */
export function movedSince(wip: IProductionWip, sinceIso: string): boolean {
  if (!hasStationWork(wip)) return false;
  return stationDates(wip).some((d) => {
    if (!d) return false;
    // Compared as the YYYY-MM-DD both sides already are, so a timestamp's
    // time-of-day and zone cannot slide a scan onto the day before.
    return toErpDate(d) >= sinceIso.slice(0, 10);
  });
}

export function hasStationWork(wip: IProductionWip): boolean {
  // Not exportableWips([wip]) — that allocates an array per WIP, and the
  // picker runs this over every one of the sixty-odd thousand.
  return wip.cancellationStatus !== "cancelled" && reachedStage(wip, "BQC2");
}

export function exportableWips(wips: IProductionWip[]): IProductionWip[] {
  // "cancelled", lower case — WipCancellationStatus is "none" | "requested" |
  // "cancelled". A capitalised literal here type-errors, which is how this was
  // caught: the comparison was always true and the filter passed everything
  // through, including cancelled work. "requested" is deliberately NOT
  // excluded: a cancellation that has only been asked for has not happened,
  // and the bundle is still live work on the floor.
  //
  // ── DEDUPED LAST, NOT ASSUMED AWAY ─────────────────────────────────────
  // This file's own header comment used to claim IProductionWip is already
  // "one row per WIP" — true of how one row's OWN fields are merged, false of
  // the LIST: `iSyn_WIPs` is documented elsewhere (bqc1MainFile.ts,
  // joPlanChecks.ts, controlTower.ts, Board.tsx, Dispatch.tsx…) to genuinely
  // hold the same WIP number more than once, and nothing upstream of this
  // function removes that. It went unnoticed here because every other
  // consumer already dedupes for its OWN purpose before this file existed —
  // until a real "Everything not yet handed over" Check (66,397 WIPs) sent
  // roughly HALF of Cutting/Assigning/Receiving/QC Passed back as "Repeated",
  // each one silently resolved by whichever copy SharePoint happened to
  // return first — no better than a coin flip on which Cutting Date/Assigned
  // Cutter/Qty actually reached their ERP.
  //
  // `woLogRecon.ts`'s own `dedupeWips` is reused rather than re-invented: it
  // already exists for this exact situation, with real WIP numbers cited as
  // evidence, and its rule — furthest pipeline stage wins, highest spId
  // breaks a tie — is "a stale duplicate cannot drag a bundle's state
  // backwards", which is the right answer for a station history being handed
  // to somebody else's database, not just for the Board and WO Log Recon.
  return dedupeWips(wips.filter((w) => w.cancellationStatus !== "cancelled"));
}

/** The five sheets, in BQC order, each with the ERP page it is uploaded to. */
export function buildErpSheets(wips: IProductionWip[]): IErpSheet[] {
  const src = exportableWips(wips);
  return [
    { stage: "BQC1", alreadyTheirs: true, name: "Manufacturing Order", importPath: "/manufacturing_order", fileName: "1_manufacturing_order.xlsx", headers: MANUFACTURING_HEADERS, rows: buildManufacturingRows(src) },
    { stage: "BQC2", name: "Cutting", importPath: "/cutting", fileName: "2_cutting.xlsx", headers: CUTTING_HEADERS, rows: buildCuttingRows(src) },
    { stage: "BQC3", name: "Assigning", importPath: "/assigning", fileName: "3_assigning.xlsx", headers: ASSIGNING_HEADERS, rows: buildAssigningRows(src) },
    { stage: "BQC4", name: "Receiving", importPath: "/receiving", fileName: "4_receiving.xlsx", headers: RECEIVING_HEADERS, rows: buildReceivingRows(src) },
    { stage: "BQC5", name: "QC Passed", importPath: "/qc_passed", fileName: "5_qc_passed.xlsx", headers: QC_PASSED_HEADERS, rows: buildQcPassedRows(src) },
    { stage: "BQC5", name: "QC Failed", importPath: "/qc_failed", fileName: "6_qc_failed.xlsx", headers: QC_FAILED_HEADERS, rows: buildQcFailedRows(src) },
  ];
}

/**
 * What goes in the zip unless someone says otherwise: BQC2 through BQC5.
 *
 * BQC1 is excluded because the ERP already maintains its own Manufacturing
 * Order table and derives its For Cutting list from it. Since their tables
 * have no unique key on WIP No., importing ours on top would duplicate every
 * row they already hold rather than update it.
 */
export function defaultSelection(sheets: IErpSheet[]): Set<string> {
  return new Set(sheets.filter((s) => !s.alreadyTheirs).map((s) => s.fileName));
}

/**
 * The note that travels inside the zip. The person doing the uploading is on
 * the fulfillment team and was not part of any conversation about this, so the
 * file has to explain itself: which file goes on which page, what order, and
 * the one thing that will bite them if nobody says it.
 */
export function buildReadme(sheets: IErpSheet[], scope: string, generatedAt: Date): string {
  const width = Math.max(...sheets.map((s) => s.fileName.length));
  const lines = sheets.map(
    (s) => `  ${s.stage}  ${s.fileName.padEnd(width)}  ->  ${s.importPath.padEnd(22)}  ${s.rows.length} rows`
  );
  return [
    "BQC1-BQC5 from i-SYNERGY (Manufacturing)",
    "========================================",
    "",
    `Scope:     ${scope}`,
    `Generated: ${generatedAt.toISOString().replace("T", " ").slice(0, 19)} UTC`,
    "",
    "Upload each file on the ERP page named beside it. Import them in the",
    "order they are numbered: the other five join against Manufacturing",
    "Order on WIP No., so that one goes first.",
    "",
    ...lines,
    "",
    "ONE FILE PER PAGE. Do not upload this whole set to one page, and do not",
    "combine the files into a single workbook with tabs -- the importer reads",
    "only the first sheet of whatever it is given, so the rest would be",
    "silently ignored or land in the wrong table.",
    "",
    "IMPORTING THE SAME SCOPE TWICE ADDS THE ROWS TWICE. There is no unique",
    "key on WIP No., so a second import does not update, it duplicates.",
    "Clear the range first if you need to re-send.",
    "",
    "A file with 0 rows still has its header row. Uploading it is harmless",
    "and changes nothing; skipping it is equally fine.",
    "",
    "Cancelled WIPs are never included. Your own cancelled-orders process",
    "owns those.",
    "",
    "Questions about a number in here belong with Manufacturing, not with",
    "whoever pressed the button.",
    "",
  ].join("\n");
}
